import React from 'react'
import type { TourStepProps } from 'antd'
import { Typography } from 'antd'
import { BookOutlined, CalendarOutlined, NotificationOutlined } from '@ant-design/icons'
import { Building2, Route, Layers, Calendar, CalendarRange, Settings2 } from 'lucide-react'
import i18next from 'i18next'

const { Text, Paragraph } = Typography

export type TourVariant = 'company' | 'companyLite' | 'accountant'

const iconStyle = { verticalAlign: '-0.125em', marginRight: 8, opacity: 0.6 } as const
const listStyle = { textAlign: 'left', paddingInlineStart: 20, marginBottom: 0 } as const

interface TourStep extends Omit<TourStepProps, 'target'> {
  /** CSS selector of the highlighted element; steps whose element isn't on the page are left out */
  selector?: string
}

const List: React.FC<{ items: (string | false)[] }> = ({ items }) => (
  <ul style={listStyle}>
    {items.filter(Boolean).map(item => (
      <li key={item as string}>
        <Text>{item}</Text>
      </li>
    ))}
  </ul>
)

const IconRow: React.FC<{ icon: React.ReactNode; text: string }> = ({ icon, text }) => (
  <Paragraph style={{ marginBottom: 4 }}>
    {icon}
    {text}
  </Paragraph>
)

export const getTourSteps = (variant: TourVariant): TourStepProps[] => {
  const t = i18next.t.bind(i18next)
  const isAccountant = variant === 'accountant'
  const canBudget = variant !== 'companyLite'

  const steps: TourStep[] = [
    {
      title: t('tour:welcome'),
      description: <Paragraph style={{ marginBottom: 0 }}>{t('tour:tips')}</Paragraph>
    },
    {
      selector: '.side-nav',
      title: t('tour:side-nav-title'),
      description: t('tour:side-nav'),
      placement: 'right'
    },
    {
      selector: '.side-nav-collapse',
      title: t('tour:side-nav-collapse-title'),
      description: t('tour:side-nav-collapse'),
      placement: 'right'
    },
    {
      selector: '.filter-toolbar',
      title: t('tour:filters-title'),
      description: (
        <div style={{ textAlign: 'left' }}>
          <Paragraph>{t('tour:filters')}</Paragraph>
          <IconRow icon={<Building2 size={14} style={iconStyle} />} text={t('tour:company-select')} />
          {canBudget && <IconRow icon={<Route size={14} style={iconStyle} />} text={t('tour:budgetscenarios')} />}
          <IconRow icon={<Layers size={14} style={iconStyle} />} text={t('tour:dimensions')} />
          <IconRow icon={<Calendar size={14} style={iconStyle} />} text={t('tour:date-filters')} />
          <IconRow icon={<CalendarRange size={14} style={iconStyle} />} text={t('tour:fiscal-year')} />
          <IconRow icon={<Settings2 size={14} style={iconStyle} />} text={t('tour:view-settings')} />
        </div>
      )
    },
    {
      selector: '.settings-menu',
      title: t('tour:settings-title'),
      description: (
        <div style={{ textAlign: 'left' }}>
          <Paragraph>{t(isAccountant ? 'tour:settings-accountant' : 'tour:settings')}</Paragraph>
          <Text strong>{t('tour:company-settings')}</Text>
          <List
            items={[
              t('tour:add-users'),
              t('tour:apis'),
              canBudget && t('tour:budgeting'),
              t('tour:keyfigures'),
              t('tour:report-distribution')
            ]}
          />
          {isAccountant && (
            <>
              <Text strong style={{ display: 'block', marginTop: 12 }}>
                {t('tour:accountant-settings')}
              </Text>
              <List
                items={[t('tour:accountant-users'), t('tour:accountant-contracts'), t('tour:accountant-subscriptions')]}
              />
            </>
          )}
        </div>
      )
    },
    {
      selector: '.notification-inbox',
      title: t('tour:notifications-title'),
      description: t('tour:notifications')
    },
    {
      selector: '.help-menu',
      title: t('tour:help-title'),
      description: (
        <div style={{ textAlign: 'left' }}>
          <Paragraph>{t('tour:help')}</Paragraph>
          <IconRow icon={<BookOutlined style={iconStyle} />} text={t('tour:help-manual')} />
          <IconRow icon={<NotificationOutlined style={iconStyle} />} text={t('header:contact')} />
          <IconRow icon={<CalendarOutlined style={iconStyle} />} text={t('header:demo')} />
        </div>
      )
    },
    {
      selector: '.avatar-menu',
      title: t('tour:profile-title'),
      description: (
        <div>
          <Paragraph>{t('tour:profile')}</Paragraph>
          <Paragraph>{t('tour:questions')}</Paragraph>
          <Paragraph style={{ marginBottom: 0 }}>{t('tour:greetings')}</Paragraph>
          <Text>{t('tour:team-finadeck')}</Text>
        </div>
      )
    }
  ]

  return steps
    .filter(({ selector }) => !selector || document.querySelector(selector))
    .map(({ selector, ...step }) => ({
      ...step,
      target: selector ? () => document.querySelector<HTMLElement>(selector)! : undefined
    }))
}
